/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState } from 'react'
import RegularTemplate from '@/components/templates/RegularTemplate'
import { useFetchItemsWithParamsV2 } from '@/hooks/fetch/useFetchItemsWithParamsV2'
import {
  getAssetLicensesBatch,
  getAssetLicensesBatchItemsByBatchId,
  startAssetLicensesBatchProcessing
} from '@/services/assets-licenses-batches'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { Spinner, useToast } from '@chakra-ui/react'
import AssetsDataLists from './components/AssetsDataLists'
import { assetLicenseBatchesUrls } from '@/configs/urls'
import HeaderBatchItem from '@/components/batches/HeaderBatchItem'
import {
  ASSET_QUEUED_FOR_LICENSING,
  ASSETS_LICENSING_BATCH,
  ASSETS_LICENSING_BATCH_DESCRIPTION,
  RETURN_TO_LICENSE_MULTIPLE_LISTED_ASSETS
} from '@/configs/strings/asset-licenses-batches.strings'
import ProcessBatch from '@/components/batches/ProcessBatch'
import { columnsTable } from './handlers'
import Styles from './styles.module.sass'
import ModalBodyProcessBatch from './components/ModalBodyProcessBatch'
import MainTotalItems from '@/components/listings/MainTotalItems'
import { setErrorCodeAndMessage } from '@/utils/fetch'
import DefaultAlert from '@/components/alerts/DefaultAlert'
import { generalBatchStates } from '@/configs/server-enums'
import { defaultToastConfig } from '@/configs/toast'
import { getBatchProcessInfo } from '@/utils/batches'
import { getFailedDescription, getOnErrorTitle, getSuccessDescription } from '@/configs/strings/batches'

const AssetLicensesBatch = () => {
  const toast = useToast()
  const [hasAlert, setHasAlert] = useState(false)
  const [inProgress, setInProgress] = useState(false)
  const [alert, setAlert] = useState()
  const { batchId } = useParams()
  const { getAccessTokenSilently: getAccessToken } = useAuth0()
  const navigate = useNavigate()
  const { processing } = generalBatchStates
  const onStartProcess = async (data, closeModal) => {
    try {
      setInProgress(true)
      setHasAlert(false)
      setAlert(null)
      closeModal()
      const assetLicensesBatchUpdated = await startAssetLicensesBatchProcessing({ id: batchId, data }, getAccessToken)

      if (assetLicensesBatchUpdated.state === processing) {
        toast({
          title: ASSETS_LICENSING_BATCH,
          description: getSuccessDescription(
            ASSETS_LICENSING_BATCH,
            assetLicensesBatchUpdated.name
          ),
          status: 'success',
          ...defaultToastConfig
        })
      }
    } catch (error) {
      toast({
        title: getOnErrorTitle(ASSETS_LICENSING_BATCH),
        description: error.response?.data?.message || getFailedDescription(ASSETS_LICENSING_BATCH),
        status: 'error',
        ...defaultToastConfig
      })
      setInProgress(false)
      setHasAlert(true)
      setAlert(setErrorCodeAndMessage(error))
    } finally {
      navigate(`/${assetLicenseBatchesUrls.base}`, { replace: true })
    }
  }
  const {
    itemsResponse: licenseMultipleAssetsBatch,
    loading: isLoading
  } = useFetchItemsWithParamsV2({
    fetchService: getAssetLicensesBatch,
    fetchParams: { id: batchId }
  })
  const {
    itemsResponse,
    loading,
    onChangePage,
    page,
    reloadItems,
    totalPages
  } = useFetchItemsWithParamsV2({
    fetchService: getAssetLicensesBatchItemsByBatchId,
    fetchParams: { id: batchId }
  })
  const {
    batchHasErrors,
    batchState,
    isHiddenForm
  } = getBatchProcessInfo(licenseMultipleAssetsBatch?.data?.state)
  const batchName = licenseMultipleAssetsBatch?.data?.name

  const HeaderBatch = () => (
    <HeaderBatchItem
      title={batchName}
      batchState={batchState}
    />

  )
  const numberAssets = itemsResponse?.total
  const costPerAsset = 0.75
  const totalCost = +numberAssets * +costPerAsset

  const rowTable = {
    totalCost,
    costPerAsset,
    numberAssets
  }
  return (
    <RegularTemplate
      backLabel={RETURN_TO_LICENSE_MULTIPLE_LISTED_ASSETS}
      backUrl={`/${assetLicenseBatchesUrls.base}`}
      childrenHeader={HeaderBatch}
      description={ASSETS_LICENSING_BATCH_DESCRIPTION}
      loadingContent={(isLoading && !licenseMultipleAssetsBatch) || inProgress}
      title={ASSETS_LICENSING_BATCH}
    >
      { !isLoading &&
        <>
          { loading
            ? <div className="text-center">
                <Spinner size="lg" />
              </div>
            : <div>
                { hasAlert &&
                  <DefaultAlert
                    status="error"
                    message={alert?.code}
                  />
                }
                <ProcessBatch
                  alertMessage={alert?.code}
                  alertStatus="error"
                  bodyModal={
                    () => <ModalBodyProcessBatch
                      costPerAsset={costPerAsset}
                      totalAssets={numberAssets}
                      totalCost={totalCost}
                    />
                  }
                  className={Styles.processBatch}
                  columnsTable={columnsTable}
                  hasAlert={hasAlert}
                  isHiddenContinueButton={isHiddenForm}
                  labelContinue="Accept Terms + License Assets"
                  labelModalOnProcess="License Assets"
                  onProcess={(data, closeModal) => onStartProcess(data, closeModal)}
                  rowTable={rowTable}
                  setHasAlert={setHasAlert}
                  titleModal="License Assets"
                />
                <MainTotalItems
                  label={ASSET_QUEUED_FOR_LICENSING}
                  total={numberAssets}
                />
                <AssetsDataLists
                  assets={itemsResponse?.data}
                  batchName={licenseMultipleAssetsBatch?.data?.name}
                  batchHasErrors={batchHasErrors}
                  batchId={batchId}
                  isHiddenForm={isHiddenForm}
                  onChangePage={onChangePage}
                  page={page}
                  reloadItems={reloadItems}
                  totalPages={totalPages}
                />
              </div>
          }
        </>
      }
    </RegularTemplate>
  )
}

export default AssetLicensesBatch
